import { graphql, readInlineData } from 'react-relay';

import { filterNull } from '@townsquare/filter-type';
import { UserCapabilityType } from '@townsquare/router/types';
import { getRoleFromCapability } from '@townsquare/router/utils';

import {
	AccessControlCapability,
	CapabilityContainer,
	utils_checkUserHasCapabilities$key,
} from './__generated__/utils_checkUserHasCapabilities.graphql';

/**
 * Plain-data capability entry. Same shape as a single element of
 * `utils_checkUserHasCapabilities$data.userCapabilities.capabilities` — but
 * read off the inlined Volt bootstrap payload instead of a Relay fragment.
 *
 * Uses the same `AccessControlCapability` / `CapabilityContainer` enum types
 * the Relay artifact exports (type-only imports, no runtime Relay).
 */
export interface CapabilityEntry {
	capability?: AccessControlCapability | null;
	capabilityContainer?: CapabilityContainer | null;
}

/**
 * Plain-data shape that `checkUserHasCapabilitiesFromVolt` reads — the fields
 * the `utils_checkUserHasCapabilities` `@inline` fragment selects, sourced
 * directly off the workspace entry in
 * `window.__TOWNSQUARE_BOOTSTRAP__.workspaces[i]`.
 *
 * Every field optional to match the forward-compat shape of
 * `BootstrapVoltPayload` (see `voltPayload.ts` for the rationale).
 */
export interface WorkspaceCapabilityFields {
	userCapabilities?: {
		capabilities?: ReadonlyArray<CapabilityEntry | null>;
	} | null;
}

export const checkUserHasCapabilities = (
	data: utils_checkUserHasCapabilities$key | null | undefined,
	requirements: UserCapabilityType[],
	allCapabilitiesRequired: boolean,
): boolean => {
	if (requirements.length === 0) {
		return true;
	}

	const workspace = readWorkspace(data);
	// The Relay `$data` is narrower (non-null fields) than the widened plain
	// `WorkspaceCapabilityFields`; structurally compatible, cast to satisfy TS.
	return checkCapabilitiesAgainstWorkspace(
		workspace as WorkspaceCapabilityFields | null | undefined,
		requirements,
		allCapabilitiesRequired,
	);
};

/**
 * Volt JSON branch sibling of `checkUserHasCapabilities`.
 *
 * Same logic, but reads the workspace's capabilities from a plain object
 * (the workspace entry from `window.__TOWNSQUARE_BOOTSTRAP__.workspaces[i]`)
 * instead of unmasking a Relay `@inline` fragment ref. Both delegate to the
 * shared `checkCapabilitiesAgainstWorkspace` helper so the predicate logic
 * stays in lockstep.
 *
 * Deletable along with the caller in
 * `apps/projects/src/bootstrap/Bootstrap.tsx` when Volt is at 100% on
 * `/project/:id` and the non-Volt path is removed.
 */
// TODO: post-Volt-100%, eliminate the GQL `checkUserHasCapabilities` above by
// pushing `readInlineData` upstream to the populator. Same shape applies to
// `initWorkspaceFromHome` in `@townsquare/workspace-store`. The hooks
// (`use-user-has-capabilities*`) that take fragment refs need the same
// cascade — separate PR.
export const checkUserHasCapabilitiesFromVolt = (
	workspace: WorkspaceCapabilityFields | null | undefined,
	requirements: UserCapabilityType[],
	allCapabilitiesRequired: boolean,
): boolean => {
	if (requirements.length === 0) {
		return true;
	}
	return checkCapabilitiesAgainstWorkspace(workspace, requirements, allCapabilitiesRequired);
};

const checkCapabilitiesAgainstWorkspace = (
	workspace: WorkspaceCapabilityFields | null | undefined,
	requirements: UserCapabilityType[],
	allCapabilitiesRequired: boolean,
): boolean => {
	if (!workspace || !workspace.userCapabilities || !workspace.userCapabilities.capabilities) {
		return false;
	}

	const userCapabilities = workspace.userCapabilities.capabilities
		.map(item => {
			if (!item || !item.capabilityContainer || !item.capability) {
				return null;
			}
			return getRoleFromCapability(item.capabilityContainer, item.capability);
		})
		.filter(filterNull);

	return allCapabilitiesRequired
		? requirements.every(capability => userCapabilities.includes(capability))
		: requirements.some(capability => userCapabilities.includes(capability));
};

const readWorkspace = (data: utils_checkUserHasCapabilities$key | null | undefined) =>
	readInlineData<utils_checkUserHasCapabilities$key>(
		graphql`
			fragment utils_checkUserHasCapabilities on WorkspaceSummary @inline {
				userCapabilities {
					capabilities {
						capability
						capabilityContainer
					}
				}
			}
		`,
		data,
	);
